import { List, Map, Repeat, Set } from 'immutable';
import {ImmutableStore} from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import BranchHierarchyTiers from '../model/BranchHierarchyTiers';
import { startOfDay } from '../utils/Date';
import { parseIntOr, parseJsonIfNeeded } from '../utils/UrlParamParsers';

class TeamPerformanceStore extends ImmutableStore {
  static storeName = 'TeamPerformanceStore';

  static handlers = {
    [Actions.TEAM_PERFORMANCE__DATA_LOAD_STARTED]: 'handleTeamPerformanceDataChanged',
    [Actions.TEAM_PERFORMANCE__DATA_LOADED]: 'handleTeamPerformanceDataChanged',
    [Actions.TEAM_PERFORMANCE__QUERY_CHANGED]: 'handleTeamPerformanceQueryChanged',
    [Actions.AUTHENTICATION__CHECK_COMPLETED]: 'handleAuth',
    [Actions.AUTHENTICATION__COMPLETED]: 'handleAuth'
  };

  constructor(dispatcher) {
    super(dispatcher);

    const now = startOfDay();
    const before = startOfDay().subtract(1, 'month');

    this.startDate = before.toDate();
    this.endDate = now.toDate();
    this.setState(this.defaultState());
  }

  defaultState = () => {
    const numBranchHierarchyTiers = this.getBranchHierarchyTerms() ? this.getBranchHierarchyTerms().size : 0;
    const numAggregationLevels = numBranchHierarchyTiers + 1;
    return {
      tieredData: Repeat(List(), numAggregationLevels).toList(),
      selectedConversationKey: 'conversationScore',
      selectedAccountKey: 'accountScore',
      aggregationLevel: numBranchHierarchyTiers,
      guideType: 'both',
      loading: false,
      customerType: 'both',
      selectedBranchHierarchy: null,
      focusedBranchHierarchyElements: null
    };
  };

  handleTeamPerformanceQueryChanged(query) {
    const defaults = this.defaultState();
    const {
      startDate = startOfDay().subtract(1, 'month'),
      endDate = startOfDay(),
      selectedConversationKey = defaults.selectedConversationKey,
      selectedAccountKey = defaults.selectedAccountKey,
      aggregationLevel = defaults.aggregationLevel,
      guideType = defaults.guideType,
      customerType = defaults.customerType,
      selectedBranchHierarchy,
      focusedBranchHierarchyElements
    } = query;

    const newState = {
      selectedAccountKey,
      selectedConversationKey,
      aggregationLevel: parseIntOr(aggregationLevel, defaults.aggregationLevel),
      guideType,
      customerType,
      selectedBranchHierarchy: parseJsonIfNeeded(selectedBranchHierarchy),
      focusedBranchHierarchyElements: parseJsonIfNeeded(focusedBranchHierarchyElements)
    };

    this.mergeState(newState);

    if (startDate || endDate) {
      this.startDate = startDate ? new Date(startDate) : this.startDate;
      this.endDate = endDate ? new Date(endDate) : this.endDate;
      this.emitChange();
    }
  }

  handleTeamPerformanceDataChanged(payload) {
    const {tieredData, loading = false} = payload;
    console.info('Updating leaderboard store state', payload);
    const newState = {
      loading,
      ...(tieredData ? { tieredData: tieredData.map((d) => d || []) } : null)
    };
    this.mergeState(newState);
  }

  getFocusedBranchHierarchyElements = () => {
    return this.get('focusedBranchHierarchyElements');
  };

  getFocusedBranchHierarchyElementAtAggregationLevel = (aggregationLevel) => {
    const focusedBranchHierarchyElements = this.getFocusedBranchHierarchyElements();
    const branchHierarchyTerms = this.getBranchHierarchyTerms();
    const branchHierarchyTiers = new BranchHierarchyTiers(branchHierarchyTerms);
    const branchHierarchyTierIndex = branchHierarchyTiers.tierIndexFromAnalyticsAggregationLevel(aggregationLevel);
    return focusedBranchHierarchyElements ? focusedBranchHierarchyElements.get(branchHierarchyTierIndex) : null;
  };

  getAggregationLevel() {
    return this.get('aggregationLevel');
  }

  getSelectedConversationKey() {
    return this.get('selectedConversationKey');
  }

  getSelectedAccountKey() {
    return this.get('selectedAccountKey');
  }

  getTieredData = () => {
    return this.get('tieredData');
  };

  getCustomerType() {
    return this.get('customerType');
  }

  getSelectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  getGuideType() {
    return this.get('guideType');
  }

  handleAuth = ({ clientInfo: { branchHierarchyTerms, branchHierarchy } = {} } = {}) => {
    if (branchHierarchyTerms && branchHierarchy) {
      this.mergeState({
        branchHierarchy,
        branchHierarchyTerms
      });
    }
  };

  getBranchHierarchy = () => {
    return this.get('branchHierarchy');
  };

  getBranchHierarchyTerms = () => {
    return this.get('branchHierarchyTerms');
  };

  getSelectedBranchHierarchy = () => {
    return this.get('selectedBranchHierarchy');
  };

  getBranchHierarchyWithFocusAtAggregationLevel = (aggregationLevel) => {
    const focusedElems = this.getFocusedBranchHierarchyElements();
    const branchHierarchyTerms = this.getBranchHierarchyTerms();
    const tiers = new BranchHierarchyTiers(branchHierarchyTerms);
    const tierIndex = tiers.tierIndexFromAnalyticsAggregationLevel(aggregationLevel);
    const focusedElem = focusedElems ? focusedElems.get(tierIndex) : null;
    const selectedBranchHierarchy = this.getSelectedBranchHierarchy() ||
      Repeat(Set(), branchHierarchyTerms ? branchHierarchyTerms.size : 0).toList();
    return selectedBranchHierarchy.map((tier, i) => {
      if (focusedElem && i === tierIndex) {
        return Set([Map({id: focusedElem})]);
      }
      return tier;
    });
  };

  isLoading() {
    return this.get('loading');
  }

  rehydrate(state) {
    super.rehydrate(state._state);
    this.startDate = new Date(state.startDate);
    this.endDate = new Date(state.endDate);
  }

  dehydrate() {
    return {
      _state: this._state,
      startDate: this.startDate,
      endDate: this.endDate
    };
  }
}

export default TeamPerformanceStore;



// WEBPACK FOOTER //
// ./src/stores/TeamPerformanceStore.js